<template>
  <div class="home-page">
    <div class="welcome-section">
      <h1 class="welcome-title">{{ msg('Welcome') }}, {{ myName }}</h1>
      <p class="welcome-subtitle">{{ msg('Apps & Pages') }}</p>
    </div>

    <div class="modules-grid">
      <div
        v-for="page in pages"
        :key="page.route"
        class="module-card"
        @click="$router.push(page.route)"
      >
        <div class="module-card-inner">
          <div class="module-icon-wrap">
            <i
              v-if="page.icon_type === 'fontawesome'"
              :class="[page.icon, page.iconClass || '']"
              class="module-icon module-icon-fa"
            />
            <feather-icon
              v-else
              :icon="page.icon"
              size="40"
              class="module-icon"
            />
          </div>
          <div class="module-content">
            <h3 class="module-title">{{ msg(page.title) }}</h3>
            <span class="module-arrow">→</span>
          </div>
        </div>
        <div class="module-card-glow" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'home',
  computed: {
    ...mapGetters('theme', ['navigation']),
    ...mapGetters('user', ['myName']),
    pages() {
      return this.navigation
        .flatMap(n => [n, ...(n.children || [])])
        .filter(n => n.route != null && n.icon != null)
    }
  }
}
</script>

<style lang="scss" scoped>
.home-page {
  padding: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
}

.welcome-section {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  .welcome-title {
    font-size: 1.75rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 0.25rem 0;
    letter-spacing: -0.02em;
  }

  .welcome-subtitle {
    font-size: 0.95rem;
    color: #64748b;
    margin: 0;
    font-weight: 500;
  }
}

.modules-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.25rem;
}

.module-card {
  position: relative;
  cursor: pointer;
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);

    .module-card-inner {
      background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
      border-color: rgba(0, 212, 255, 0.3);
      box-shadow: 0 12px 40px -12px rgba(0, 0, 0, 0.15);
    }

    .module-icon-wrap {
      background: linear-gradient(135deg, rgba(0, 212, 255, 0.12) 0%, rgba(0, 212, 255, 0.06) 100%);
      color: #0891b2;
    }

    .module-arrow {
      opacity: 1;
      transform: translateX(0);
    }

    .module-card-glow {
      opacity: 0.5;
    }
  }

  &:active {
    transform: translateY(-2px);
  }
}

.module-card-inner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1.75rem 1.25rem;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
}

.module-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 14px;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #475569;
  transition: all 0.2s ease;
}

.module-icon {
  color: inherit;

  &.module-icon-fa {
    font-size: 2rem;
  }

  :deep(svg) {
    stroke-width: 2;
  }
}

.module-content {
  text-align: center;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.module-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #334155;
  margin: 0;
  line-height: 1.3;
  flex: 1;
  min-width: 0;
}

.module-arrow {
  font-size: 1rem;
  color: #00d4ff;
  opacity: 0;
  transform: translateX(-4px);
  transition: all 0.2s ease;
}

.module-card-glow {
  position: absolute;
  inset: -1px;
  border-radius: 16px;
  background: radial-gradient(circle at 50% 0%, rgba(0, 212, 255, 0.08) 0%, transparent 70%);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;
}

/* Dark layout support */
.dark-layout .home-page {
  .welcome-section {
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }

  .welcome-title {
    color: #e2e8f0;
  }

  .welcome-subtitle {
    color: #94a3b8;
  }

  .module-card-inner {
    background: rgba(30, 41, 59, 0.6);
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  .module-card:hover .module-card-inner {
    background: rgba(51, 65, 85, 0.5);
    border-color: rgba(0, 212, 255, 0.25);
    box-shadow: 0 12px 40px -12px rgba(0, 0, 0, 0.4);
  }

  .module-icon-wrap {
    background: linear-gradient(135deg, rgba(51, 65, 85, 0.8) 0%, rgba(30, 41, 59, 0.8) 100%);
    color: #94a3b8;
  }

  .module-card:hover .module-icon-wrap {
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.15) 0%, rgba(0, 212, 255, 0.05) 100%);
    color: #22d3ee;
  }

  .module-title {
    color: #e2e8f0;
  }
}
</style>
